import { MouseEvent as ReactMouseEvent } from "react";

import { faStar } from "@fortawesome/pro-regular-svg-icons";

import { format } from "date-fns";

import { Icon } from "@/components";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { TableColumn } from "@/components/Table/Table";
import { DeepResearchStatusIcon } from "@/components/DeepResearchStatusIcon/DeepResearchStatusIcon";
import { SortParams } from "@/types/types";

import { ContactProspect } from "../schema/contacts";
import { getOrderStatus } from "./column-utils";

// ─── Location / created-at / scoring / research columns (list-detail parity) ──
//
// Kept out of the over-cap `allColumns.tsx`. Each reads the `ContactProspect` row
// directly (the Contacts equivalent of the list's `row.prospect`).

const renderCopyableText = (
	value: string | null | undefined,
	handleCopyClick: (value: string) => void
) => (
	<button
		className="cursor-pointer text-sm text-gray-700"
		type="button"
		onClick={(e: ReactMouseEvent) => {
			e.preventDefault();
			e.stopPropagation();
			if (value) {
				handleCopyClick(value);
			}
		}}
	>
		{value || "-"}
	</button>
);

export const renderContactPersonCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.city";

	return {
		title: ts("table.city"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactPersonCity",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => renderCopyableText(row.city, handleCopyClick),
	};
};

export const renderContactPersonStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.state";

	return {
		title: ts("table.state"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactPersonState",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => renderCopyableText(row.state, handleCopyClick),
	};
};

export const renderContactCompanyCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.city";

	return {
		title: ts("table.companyCity"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanyCity",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => renderCopyableText(row.organization?.city, handleCopyClick),
	};
};

export const renderContactCompanyStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.state";

	return {
		title: ts("table.companyState"),
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCompanyState",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => renderCopyableText(row.organization?.state, handleCopyClick),
	};
};

export const renderContactCreatedAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.createdAt";

	return {
		title: ts("table.createdAt"),
		width: "fit-content",
		minWidth: "150px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contactCreatedAt",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => (
			<span className="text-sm text-gray-700">
				{row.createdAt ? format(new Date(row.createdAt), "yyyy-MM-dd HH:mm") : "-"}
			</span>
		),
	};
};

export const renderContactPersonaScoreColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: ts("table.personaScore"),
		iconRender: () => <Icon className="ml-1 text-action-400" icon={faStar} />,
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "contactPersonaScore",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const result = row.scorePersonaCategoryResult;

			return (
				<div className="flex items-center">
					<Tooltip
						content={result?.personaScoringSummary}
						disabled={!result?.personaScoringSummary}
					>
						<span className="text-sm text-gray-700">{result?.personaScore || "-"}</span>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderContactCompanyScoreColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: ts("table.companyScore"),
		iconRender: () => <Icon className="ml-1 text-action-400" icon={faStar} />,
		width: "fit-content",
		minWidth: "120px",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "contactCompanyScore",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const result = row.scorePersonaCategoryResult;

			return (
				<div className="flex items-center">
					<Tooltip
						content={result?.companyScoringSummary}
						disabled={!result?.companyScoringSummary}
					>
						<span className="text-sm text-gray-700">{result?.companyScore || "-"}</span>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderContactResearchColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("table.research"),
		width: "fit-content",
		minWidth: "80px",
		uid: "contactResearch",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ContactProspect) => {
			const deepResearch = row.deepResearchResponse;
			const isDone = !!deepResearch?.response && Object.keys(deepResearch.response).length > 0;
			const createdAt = deepResearch?.createdAt
				? format(new Date(deepResearch.createdAt), "yyyy-MM-dd HH:mm")
				: "";
			const tooltipText = isDone
				? `Deep Research / done${createdAt ? ` | ${createdAt}` : ""}`
				: "Deep Research / not started";

			return (
				<DeepResearchStatusIcon
					fitAssessment={deepResearch?.fitAssessment}
					isDone={isDone}
					isRunning={false}
					tooltipText={tooltipText}
				/>
			);
		},
	};
};
